import { AsyncPipe, NgClass, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { createSelector, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, fromEvent, Observable, of, tap, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap } from 'rxjs/operators';

import { CategoryViewType, MotorizationVehicle, Vehicle } from 'src/app/api/owcc/models';
import { AppDomContextService } from 'src/app/app-dom-context.service';
import { appInject } from 'src/app/app-injector';
import { ConfigurationService } from 'src/app/features/configuration/configuration.service';
import { Components, Modules } from 'src/app/ng.types';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { AcknowledgementContainerComponent } from 'src/app/shared/acknowledgement-container/acknowledgement-container.component';
import { FinanceService } from 'src/app/shared/financing/finance.service';
import { NavigationGroup } from 'src/app/shared/navigation/navigation-group.interface';
import { NavigationItem } from 'src/app/shared/navigation/navigation-item.interface';
import { NbaBarModule } from 'src/app/shared/nba-bar/nba-bar.module';
import { StageWithSidebarComponent } from 'src/app/shared/stage/stage-with-sidebar/stage-with-sidebar.component';
import {
  startSetNavigationGroups,
  updateDisplayShadowUnderStage,
  updateIsRowLayoutUsed,
  updateStage
} from 'src/app/store/common/common.actions';
import {
  fromCarl,
  fromMq,
  fromNavigation,
  selectConfigurationVehicle,
  selectCurrentMotorization,
  selectHeaderInfoHeight,
  selectIsInStandaloneMode,
  selectIsLoading,
  selectIsModalOpen,
  selectIsNextStepButtonVisible,
  selectIsRowLayoutUsed,
  selectIsSummarySidebarShown,
  selectThresholdRange,
  selectVehicleIdForUpcomingConfiguration
} from 'src/app/store/common/common.selectors';
import { seamlessStoreFns } from 'src/app/store/common/common.utils';
import { selectIsPlsInitialized } from 'src/app/store/mbcc/mbcc.selectors';
import {
  selectIsMotorizationPage,
  selectIsNotPreconfigPage,
  selectIsSummaryPage,
  selectRouteState
} from 'src/app/store/router/router.selectors';
import { noop } from 'src/app/utils/functions/noop';
import { APP_CONTAINER_ID, CONTENT_SCROLL_ANCHOR_ID, HEADER_SCROLL_ANCHOR_ID } from 'src/app/utils/global-constants';
import { WINDOW } from 'src/app/utils/window.token';

import { FinanceCalculatorModule } from '../../shared/financing/finance-calculator/finance-calculator.module';
import { FooterComponent } from '../../shared/footer/footer.component';
import { HeaderInfoComponent } from '../../shared/header-info/header-info.component';
import { HeaderComponent } from '../../shared/header/header.component';
import { LoadingSpinnerComponent } from '../../shared/loading-spinner/loading-spinner.component';
import { ModalHostComponent } from '../../shared/modal/modal-host/modal-host.component';
import { NavigationComponent } from '../../shared/navigation/navigation.component';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { SelectablesSearchHostComponent } from '../../shared/selectables-search-host/selectables-search-host.component';
import { AcknowledgementConfig } from '../services/acknowledgement/acknowledgement.service';
import { ActivatedRouteState } from '../services/activated-route/activated-route-state.interface';
import { LinkService } from '../services/link/link.service';
import { MediaQueryThreshold } from '../services/match-media/media-query-threshold.enum';

export const STAGE_CONTAINER_WRAPPER_ID = 'cc-stage-container-wrapper-id'; // container that stretches to the next step button, in order to allow scrolling
const INITIAL_MOBILE_NAVIGATION_HEIGHT = 52;
export const RESIZE_DEBOUNCE_TIME = 100;

// exported for testing purposes
export const selectDisplayStage = createSelector(
  selectRouteState,
  routeState => routeState?.isMotorization || routeState?.isVehicleLength || routeState?.isConfiguration
);

const selectNavigationSteps = createSelector(
  fromNavigation.selectNavigationGroups,
  (navigationGroups: NavigationGroup[] = []): NavigationItem[] => navigationGroups.flatMap(group => group.children)
);

const selectNextNavigationIndex = createSelector(
  fromNavigation.selectCurrentNavigationElementIndex,
  index => (index ?? 0) + 1
);

const selectNextNavigationItem = createSelector(
  selectNextNavigationIndex,
  selectNavigationSteps,
  selectCurrentMotorization,
  (nextIndex, navigationSteps) => navigationSteps[nextIndex]
);

@Component({
  selector: 'cc-app-container',
  templateUrl: './app-container.component.html',
  styleUrl: './app-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    HeaderComponent,
    HeaderInfoComponent,
    SelectablesSearchHostComponent,
    LoadingSpinnerComponent,
    ModalHostComponent,
    FooterComponent,
    NavigationComponent,
    NbaBarModule,
    StageWithSidebarComponent,
    Modules(FinanceCalculatorModule),
    NgIf,
    NgTemplateOutlet,
    NgStyle,
    NgClass,
    LetDirective,
    Components(AcknowledgementContainerComponent)
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppContainerComponent implements OnInit {
  @ViewChild('headerScrollAnchorReference') public headerScrollAnchorReference?: ElementRef<HTMLLinkElement>;
  @ViewChild('appContainer') public appContentContainer?: ElementRef<HTMLElement>;
  @ViewChild('appContainerContentStageContainer') public appStageContainer?: ElementRef<HTMLElement>;

  public readonly appContainerId = APP_CONTAINER_ID;
  public readonly idMainContainer = STAGE_CONTAINER_WRAPPER_ID;
  public readonly headerScrollAnchorId = HEADER_SCROLL_ANCHOR_ID;
  public readonly contentScrollAnchorId = CONTENT_SCROLL_ANCHOR_ID;
  public readonly mediaQueryThreshold = MediaQueryThreshold;
  public navigationHeight = INITIAL_MOBILE_NAVIGATION_HEIGHT;

  public vehicleForUpcomingConfiguration: Vehicle | undefined;

  public readonly nextVehicleId$ = this.store.select(selectVehicleIdForUpcomingConfiguration);

  public readonly isLoadingReflectingSpinnerState$ = this.store.select(selectIsLoading);

  public readonly isFcisEnabledForCustomerType$: Observable<boolean> =
    this.financeService.isFcisEnabledForCurrentCustomerType();

  /*
    If PLS is in use, it has to be loaded _before_ FCIS, to ensure FCIS is working properly
   **/
  public isPlsInitializedOrNotEnabled$: Observable<boolean> = this.store.select(
    createSelector(
      selectIsPlsInitialized,
      fromCarl.selectConfig('financial.usePriceDisplayComponent'),
      (isPlsInitialized, isPlsEnabled) => isPlsInitialized || !isPlsEnabled
    )
  );

  public readonly useFcis$: Observable<boolean> = combineLatest([
    this.isFcisEnabledForCustomerType$,
    this.isPlsInitializedOrNotEnabled$
  ]).pipe(
    map(
      ([isFcisEnabledForCurrentCustomerType, isPlsInitializedOrNotEnabled]) =>
        isFcisEnabledForCurrentCustomerType && isPlsInitializedOrNotEnabled
    )
  );

  public readonly selectIsMotorizationAndDesktop = createSelector(
    selectIsMotorizationPage,
    fromMq.selectIsMq4Min,
    (isMotorization, isMq4Min) => isMotorization && isMq4Min
  );

  public readonly isLastNavigationStep$ = this.store.select(
    createSelector(
      fromNavigation.selectActiveNavigationStep,
      fromNavigation.selectLastNavigationStep,
      (currentStep, lastStep) => currentStep?.item === lastStep.item
    )
  );

  public isMotorizationPage = false;
  public isVehicleLengthPage = false;
  public isPreconfigPage = false;

  public showNavigation = false;
  public isErrorPage = false;

  public readonly isMotorizationAndDesktop$ = this.store.select(this.selectIsMotorizationAndDesktop);

  public thresholdRange!: MediaQueryThreshold.MQ3max | MediaQueryThreshold.MQ4 | MediaQueryThreshold.MQ5min;

  public appContainerHeight = 0;
  public isInStandaloneMode$ = this.store.select(selectIsInStandaloneMode);

  public readonly isSidebarShown$: Observable<boolean> = this.store.select(selectIsSummarySidebarShown);

  public readonly acknowledgementConfig$ = this.store
    .pipe(fromCarl.selectConfigOnce<AcknowledgementConfig>('acknowledgement'))
    .pipe(filter(acknowledgementConfig => !!acknowledgementConfig));

  public readonly nextNavigationItem$ = this.store.select(selectNextNavigationItem).pipe(
    switchMap((nextNavigationItem: NavigationItem) => {
      if (!nextNavigationItem) {
        return of();
      }

      const getNextStepLabel = (navItem: NavigationItem): Observable<string> =>
        this.store
          .select(fromCarl.selectTranslation('common.goToNextStep', navItem.translatedName ?? navItem.name))
          .pipe(first());

      const getOverriddenOrFallbackNextStepLabel = (navItem: NavigationItem): Observable<string> =>
        this.store
          .select(fromCarl.selectTranslation(`buttonLabelOverrides.${navItem.pathParams?.configurationStep}`))
          .pipe(
            first(),
            switchMap(overriddenButtonLabel =>
              overriddenButtonLabel ? of(overriddenButtonLabel) : getNextStepLabel(navItem)
            )
          );

      const needsTranslation = nextNavigationItem.isTranslated;
      const nextStepLabel$ = needsTranslation
        ? getOverriddenOrFallbackNextStepLabel(nextNavigationItem)
        : getNextStepLabel(nextNavigationItem);

      return nextStepLabel$.pipe(map(label => ({ navItem: nextNavigationItem, label })));
    }),
    switchMap(nextNavigationItem =>
      combineLatest([
        this.linkService.getLink(nextNavigationItem.navItem.routePath, nextNavigationItem.navItem.pathParams),
        of(nextNavigationItem.label)
      ])
    ),
    map(([routerLink, label]) => ({ routerLink: routerLink, label: label }))
  );

  public readonly isNextStepButtonVisible$ = this.store.select(selectIsNextStepButtonVisible);

  public readonly nextStepButton$ = combineLatest([
    this.nextNavigationItem$,
    this.isLastNavigationStep$,
    this.isNextStepButtonVisible$
  ]).pipe(
    map(([nextNavigationItem, isLastStep, isNextStepButtonVisible]) => ({
      nextNavigationItem,
      showButton: !isLastStep && nextNavigationItem && isNextStepButtonVisible
    }))
  );

  public isRowLayoutUsed = false;
  public isSingleChoiceView = false;
  public navigationIsOpen = false;

  public readonly displayStage$ = this.store.select(selectDisplayStage);
  public categoryViewType: CategoryViewType = CategoryViewType.Default;

  public readonly mainContainerTopPosition$ = this.store
    .select(selectHeaderInfoHeight)
    .pipe(map(height => (this.thresholdRange === MediaQueryThreshold.MQ3max ? this.navigationHeight : height)));

  public readonly isSummary$: Observable<boolean> = this.store.select(selectIsSummaryPage);
  public readonly isAppContainerHeaderStep$: Observable<boolean> = this.store.select(selectIsNotPreconfigPage);

  private readonly scrollEventSubject = new BehaviorSubject<boolean>(false);
  private readonly isMq3Max = this.store.select(fromMq.selectIsMq3Max);
  private readonly isRowLayout = this.store.select(selectIsRowLayoutUsed);

  public constructor(
    private readonly configurationService: ConfigurationService,
    private readonly appDomContext: AppDomContextService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly linkService: LinkService,
    private readonly navigationService: NavigationService,
    private readonly financeService: FinanceService,
    @Inject(WINDOW) public readonly window: Window
  ) {}

  public get mainContainerHeight(): number {
    const stickyContainer = this.appDomContext.shadowRoot?.getElementById(STAGE_CONTAINER_WRAPPER_ID);
    if (!stickyContainer) return 0;
    const stickyContainerRect = stickyContainer.getBoundingClientRect().toJSON() as DOMRectReadOnly;
    return stickyContainerRect?.height || 0;
  }

  @HostListener('window:scroll')
  public onScroll(): void {
    this.scrollEventSubject.next(true);
  }

  public ngOnInit(): void {
    this.updatePropertiesBasedOnRouteState();
    this.updatePropertiesBasedOnMq();
    this.updatePropertiesBasedOnCategory();

    this.observeStoreToUpdateUpcomingVehicle();
    this.observeStoreToUpdateConfiguredVehicle();
    this.obseserveStoreToUpdateModalOpenViews();

    this.setupSeamlessPublicStore();
    this.setupScrollListener();

    fromEvent(this.window, 'resize')
      .pipe(debounceTime(RESIZE_DEBOUNCE_TIME), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(() => {
        this.calculateAppContainerStyles();
      });

    // Execute once at the beginning
    // Also: wrapped (setTimeout) in order to prevent ExpressionChangedAfterItHasBeenCheckedError.
    // This is not nice but probably ok as long as the AB test is running.
    timer(0).subscribe(() => {
      this.calculateAppContainerStyles();
    });
  }

  // dynamically set min-height to enable automatic scrolling to app header
  public calculateAppContainerStyles(): void {
    const bodyPosition = this.window.document.body.getBoundingClientRect()?.top || 0;
    const elementPosition = this.headerScrollAnchorReference?.nativeElement?.getBoundingClientRect()?.top || 0;
    const viewPortHeight = this.window.innerHeight || 0;
    this.appContainerHeight = viewPortHeight + elementPosition - bodyPosition;
    this.changeDetectorRef.markForCheck();
  }

  public onNavigationIsOpen(navigationIsOpen: boolean): void {
    this.navigationIsOpen = navigationIsOpen;
  }

  public checkIfSingleChoiceView(): boolean {
    return this.categoryViewType === CategoryViewType.SingleChoice;
  }

  public onNextStep(link: string): void {
    this.router.navigate([link], { queryParams: { vehicleId: null }, queryParamsHandling: 'merge' }).catch(noop);
  }

  private setupSeamlessPublicStore(): void {
    if (seamlessStoreFns.isSeamlessStoreAvailable()) {
      seamlessStoreFns.initOWCCPublicStoreConnection().catch(noop);
    }
  }

  private updatePropertiesBasedOnCategory(): void {
    this.configurationService.currentSelectedCategory$
      .pipe(takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(currentCategory => {
        this.categoryViewType = currentCategory?.categoryViewType ?? CategoryViewType.Default;
        this.changeDetectorRef.markForCheck();
        this.determineLayout();
      });
  }

  private updatePropertiesBasedOnRouteState(): void {
    this.store
      .select(selectRouteState)
      .pipe(
        filter((routeState: ActivatedRouteState | undefined): routeState is ActivatedRouteState => !!routeState),
        takeUntilDestroyed(appInject(DestroyRef))
      )
      .subscribe((routeState: ActivatedRouteState): void => {
        this.isMotorizationPage = routeState.appRoutePath === AppRoutePath.Motorization;
        this.isVehicleLengthPage = routeState.appRoutePath === AppRoutePath.VehicleLength;
        this.isPreconfigPage = routeState.appRoutePath === AppRoutePath.Preconfig;
        this.isErrorPage = routeState.appRoutePath === AppRoutePath.ErrorPage;
        const isConfigurationPage = routeState.appRoutePath === AppRoutePath.Configuration;
        this.showNavigation = this.isMotorizationPage || this.isVehicleLengthPage || isConfigurationPage;

        this.changeDetectorRef.markForCheck();
      });
  }

  private updatePropertiesBasedOnMq(): void {
    this.store
      .select(selectThresholdRange)
      .pipe(
        takeUntilDestroyed(appInject(DestroyRef)),
        filter(
          (mqRange): mqRange is MediaQueryThreshold.MQ3max | MediaQueryThreshold.MQ4 | MediaQueryThreshold.MQ5min =>
            mqRange !== undefined
        )
      )
      .subscribe(mqRange => {
        const updateNavigationHeightForMq1To3 = (): void => {
          if (mqRange === MediaQueryThreshold.MQ3max) {
            this.navigationHeight =
              this.navigationService.height > 0 ? this.navigationService.height : this.navigationHeight;
            this.changeDetectorRef.markForCheck();
          }
        };

        this.thresholdRange = mqRange;
        this.changeDetectorRef.markForCheck();

        updateNavigationHeightForMq1To3();
        this.determineLayout();
      });
  }

  private observeStoreToUpdateUpcomingVehicle(): void {
    const observeStoreToUpdateUpcomingVehicle = (): Observable<MotorizationVehicle> =>
      this.store.select(selectCurrentMotorization).pipe(
        filter((vehicle): vehicle is MotorizationVehicle => vehicle !== undefined),
        tap(vehicle => {
          this.store.dispatch(startSetNavigationGroups({ componentCategories: vehicle.navigationCategories }));
        })
      );
    const dispatchUpdateStageAction = (configuredOrUpcomingConfigurationVehicle: Vehicle): void => {
      this.store.dispatch(
        updateStage({ stage: configuredOrUpcomingConfigurationVehicle?.stageV2, enforceReset: false })
      );
    };

    observeStoreToUpdateUpcomingVehicle()
      .pipe(takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(vehicle => {
        this.vehicleForUpcomingConfiguration = vehicle;
        dispatchUpdateStageAction(vehicle);
      });
  }

  private obseserveStoreToUpdateModalOpenViews(): void {
    this.store
      .select(selectIsModalOpen)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  private observeStoreToUpdateConfiguredVehicle(): void {
    this.store
      .select(selectConfigurationVehicle)
      .pipe(
        filter((vehicle: Vehicle | null | undefined): vehicle is Vehicle => !!vehicle),
        takeUntilDestroyed(appInject(DestroyRef))
      )
      .subscribe(vehicle => (this.vehicleForUpcomingConfiguration = vehicle));
  }

  private determineLayout(): void {
    this.isRowLayoutUsed = this.categoryViewType === CategoryViewType.SingleChoice;

    this.isSingleChoiceView = this.checkIfSingleChoiceView();

    this.store.dispatch(updateIsRowLayoutUsed({ isRowLayoutUsed: this.isRowLayoutUsed }));

    this.changeDetectorRef.markForCheck();
  }

  private setupScrollListener(): void {
    combineLatest([this.isMq3Max, this.isRowLayout, this.scrollEventSubject])
      .pipe(
        map(([isMq3Max, isRowLayout]) => isMq3Max && isRowLayout && this.isContentHidden()),
        distinctUntilChanged(),
        tap(v => {
          this.store.dispatch(updateDisplayShadowUnderStage({ displayShadowUnderStage: v }));
        }),
        takeUntilDestroyed(appInject(DestroyRef))
      )
      .subscribe();
  }

  private isContentHidden(): boolean {
    if (!this.appStageContainer || !this.appContentContainer) {
      return false;
    }

    const appStageContainerRect = this.appStageContainer.nativeElement.getBoundingClientRect();
    const appContentContainerRect = this.appContentContainer.nativeElement.getBoundingClientRect();

    const minHeightToScroll = appStageContainerRect?.height + this.navigationHeight;

    // If que container Y positions is lower than Stage and top menu height
    // that means, the container content is behind the stage
    return appContentContainerRect.y < minHeightToScroll;
  }
}
